import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import { useUID } from 'react-uid';
import { twJoin, twMerge } from 'tailwind-merge';

import { noop } from 'lib/utils';

import Icon from '../Icon';

const Checkbox = forwardRef(
  (
    {
      id: customId = null,
      checked = false,
      className,
      defaultChecked = null,
      disabled = false,
      label,
      onChange = noop,
      onClick = noop,
      ...rest
    },
    ref
  ) => {
    const uniqueId = useUID();
    const id = customId || uniqueId;

    return (
      <div
        className={twMerge(
          'relative inline-flex select-none items-center text-sm text-neutral-low-400',
          'dark:text-neutral-high-200 md:text-base',
          className
        )}
        {...rest}
      >
        {checked && (
          <div
            aria-hidden="true"
            className={twJoin(
              'absolute left-[-2px] top-[0.01rem] z-10 md:top-[0.05rem]',
              (checked || defaultChecked) && 'text-neutral-high-100',
              disabled
                ? 'cursor-not-allowed text-neutral-high-500 dark:text-neutral-low-100'
                : 'cursor-pointer'
            )}
            onClick={onClick}
          >
            <Icon
              className="text-neutral-high-100 dark:text-neutral-high-100"
              name="check"
              size="size2"
            />
          </div>
        )}
        <input
          ref={ref}
          checked={checked}
          className="peer absolute m-[-0.0625rem] hidden size-[0.0625rem] overflow-auto whitespace-normal border-0 p-0"
          defaultChecked={defaultChecked || undefined}
          disabled={disabled}
          id={id}
          type="checkbox"
          suppressHydrationWarning
          onChange={(e) => disabled || onChange(e)}
          /**
           * Is used to suppress errors related of inconsistencies between
           * client-side and server-side of ids generated by 'react-uid'
           */
          onClick={onClick}
        />
        <label
          className={twJoin(
            'ml-1 pl-5',
            'before:absolute before:left-0 before:top-[0.0625rem] before:mr-1 before:block',
            'before:size-4 before:rounded-1 before:border-[0.0625rem] before:border-solid',
            'before:border-neutral-low-100 before:transition-all before:ease-in before:content-empty',
            'peer-focus:after:absolute peer-focus:after:left-0 peer-focus:after:top-[0.0625rem] md:before:top-1',
            'peer-focus:after:block peer-focus:after:size-4 peer-focus:after:rounded-1',
            'peer-focus:after:shadow-[0_0_0_2px_rgba(255,255,255,0.9),0_0_0_3px_theme(colors.primary.200)]',
            'peer-focus:after:content-empty md:peer-focus:after:top-1',
            checked
              ? 'border-primary-300 before:border-primary-300 before:bg-primary-300'
              : 'before:bg-neutral-high-100 before:dark:border-neutral-high-200 before:dark:bg-neutral-low-300',
            disabled ? 'cursor-not-allowed' : 'cursor-pointer',
            disabled &&
              (checked
                ? 'border-primary-200 before:border-primary-200 before:bg-primary-200 dark:border-primary-200 dark:before:border-primary-200 dark:before:bg-primary-200'
                : 'before:border-neutral-high-500 before:bg-neutral-high-300 before:dark:border-neutral-low-100 dark:before:bg-neutral-low-300')
          )}
          htmlFor={id}
        >
          {label}
        </label>
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

Checkbox.propTypes = {
  /**
   * Renders a checked checkbox
   */
  checked: PropTypes.bool,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Default value for uncontrolled component
   */
  defaultChecked: PropTypes.bool,
  /**
   * Renders a disabled checkbox
   */
  disabled: PropTypes.bool,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Custom identifier for the checkbox input. If this is not
   * provided, we will add a unique id with the prefix `checkbox_`
   */
  id: PropTypes.string,
  /**
   * Text to be render as label
   */
  label: PropTypes.string.isRequired,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback function on change
   */
  onChange: PropTypes.func,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback function on click
   */
  onClick: PropTypes.func,
};

export default Checkbox;
